import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { 
  Clock, DollarSign, TrendingDown, Sparkles, Play, RefreshCw,
  ChevronRight, Lightbulb, Calculator, History, Zap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#059669', '#0EA5E9', '#F59E0B', '#E11D48', '#8B5CF6'];

const getDemoTimeMachineData = () => ({
  summary: {
    total_scenarios: 5,
    total_actual_cost: 284500,
    total_simulated_cost: 198400,
    total_potential_savings: 86100,
    avg_savings_percent: 30.2,
  },
  scenarios: [
    { type: 'spot_instances', name: 'Spot Instances', actual_cost: 118500, simulated_cost: 47400, savings: 71100, savings_percent: 60, months_analyzed: 6, confidence: 0.87 },
    { type: 'reserved_instances', name: 'Reserved Instances (1yr)', actual_cost: 118500, simulated_cost: 77025, savings: 41475, savings_percent: 35, months_analyzed: 6, confidence: 0.95 },
    { type: 'right_sizing', name: 'Right-sizing', actual_cost: 284500, simulated_cost: 227600, savings: 56900, savings_percent: 20, months_analyzed: 6, confidence: 0.91 },
    { type: 'scheduling', name: 'Dev/Test Scheduling', actual_cost: 95200, simulated_cost: 38080, savings: 57120, savings_percent: 60, months_analyzed: 6, confidence: 0.93 },
    { type: 'storage_tiering', name: 'Storage Tiering', actual_cost: 42300, simulated_cost: 29610, savings: 12690, savings_percent: 30, months_analyzed: 6, confidence: 0.89 },
  ],
  monthly_breakdown: [
    { month: 'Oct', actual: 312000, simulated: 218400 },
    { month: 'Nov', actual: 305000, simulated: 213500 },
    { month: 'Dec', actual: 298000, simulated: 208600 },
    { month: 'Jan', actual: 292000, simulated: 204400 },
    { month: 'Feb', actual: 288000, simulated: 201600 },
    { month: 'Mar', actual: 284500, simulated: 199150 },
  ]
});

const CostTimeMachine = () => {
  const { shouldShowData } = useDemoData();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  
  // Simulation parameters
  const [scenarioType, setScenarioType] = useState('spot_instances');
  const [monthsBack, setMonthsBack] = useState(6);
  const [coverage, setCoverage] = useState(70);

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/time-machine/scenarios`);
      const respData = response.data;
      if (respData.summary?.total_scenarios === 0 && shouldShowData) {
        setData(getDemoTimeMachineData());
      } else {
        setData(respData);
      }
    } catch (error) {
      console.error('Failed to fetch Time Machine data:', error);
      if (shouldShowData) {
        setData(getDemoTimeMachineData());
      } else {
        toast.error('Failed to load Time Machine scenarios');
      }
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const response = await axios.post(
        `${API_URL}/time-machine/simulate?scenario_type=${scenarioType}&months_back=${monthsBack}&coverage_percent=${coverage}`
      );
      setSimulationResult(response.data);
      toast.success('Simulation complete!');
    } catch (error) {
      console.error('Simulation failed:', error);
      toast.error('Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="time-machine-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Card key={i}><CardContent className="p-6"><Skeleton className="h-20" /></CardContent></Card>)}
        </div>
        <Card><CardContent className="p-6"><Skeleton className="h-80" /></CardContent></Card>
      </div>
    );
  }

  const summary = data?.summary || {};
  const scenarios = data?.scenarios || [];
  const insights = data?.insights || [];

  // Chart data
  const scenarioChartData = scenarios.map(s => ({
    name: s.name.split(' ').slice(0, 3).join(' '),
    actual: s.actual_cost,
    simulated: s.simulated_cost,
    savings: s.savings
  }));

  return (
    <div className="p-6 space-y-6" data-testid="time-machine">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
            <Clock className="w-7 h-7 text-amber-500" />
            Cost Time Machine
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            What if you had made different decisions? Explore historical scenarios.
          </p>
        </div>
        <Button variant="outline" onClick={fetchScenarios} className="gap-2" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Banner */}
      <Card className="bg-amber-500 bg-gradient-to-r from-amber-500 to-orange-500 border-0" data-testid="summary-banner">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-amber-100 text-sm font-medium">Total Missed Savings Opportunity</p>
              <p className="text-4xl font-bold text-white mt-1">
                {formatCurrency(summary.total_potential_savings || 0)}
              </p>
              <p className="text-amber-100 text-sm mt-1">
                Across {summary.total_scenarios || 0} historical scenarios
              </p>
            </div>
            <div className="flex gap-4">
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur">
                <p className="text-amber-100 text-xs">Actual Spent</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(summary.total_actual_cost || 0)}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur">
                <p className="text-amber-100 text-xs">Could Have Been</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(summary.total_simulated_cost || 0)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Simulation Tool */}
      <Card data-testid="simulation-tool">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="w-5 h-5 text-emerald-500" />
            Run Your Own Simulation
          </CardTitle>
          <CardDescription>Explore what-if scenarios with your actual cost data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Scenario Type</label>
              <Select value={scenarioType} onValueChange={setScenarioType}>
                <SelectTrigger className="mt-2" data-testid="scenario-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spot_instances">Spot Instances (65% off)</SelectItem>
                  <SelectItem value="reserved_instances">Reserved Instances (30% off)</SelectItem>
                  <SelectItem value="rightsizing">Rightsizing (20% off)</SelectItem>
                  <SelectItem value="arm_migration">ARM Migration (20% off)</SelectItem>
                  <SelectItem value="storage_tiering">Storage Tiering (40% off)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Time Period: {monthsBack} months</label>
              <Slider
                value={[monthsBack]}
                onValueChange={(v) => setMonthsBack(v[0])}
                min={1}
                max={12}
                step={1}
                className="mt-4"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Coverage: {coverage}%</label>
              <Slider
                value={[coverage]}
                onValueChange={(v) => setCoverage(v[0])}
                min={10}
                max={100}
                step={10}
                className="mt-4"
              />
            </div>
            <div className="flex items-end">
              <Button 
                onClick={runSimulation} 
                disabled={simulating}
                className="w-full gap-2 bg-cyan-600 hover:bg-cyan-700"
                data-testid="run-simulation-btn"
              >
                {simulating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Run Simulation
              </Button>
            </div>
          </div>

          {/* Simulation Result */}
          {simulationResult && (
            <div className="mt-6 p-4 rounded-lg bg-emerald-50 dark:bg-cyan-900/20 border border-emerald-200 dark:border-cyan-800" data-testid="simulation-result">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-cyan-900 dark:text-cyan-100">Simulation Result</h4>
                  <p className="text-sm text-emerald-700 dark:text-cyan-300 mt-1">
                    {simulationResult.recommendation}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-emerald-600">{formatCurrency(simulationResult.potential_savings)}</p>
                  <p className="text-sm text-slate-400">potential savings ({simulationResult.savings_percent}%)</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="p-3 rounded bg-white dark:bg-slate-800">
                  <p className="text-xs text-slate-400">Actual Cost</p>
                  <p className="font-semibold">{formatCurrency(simulationResult.actual_cost)}</p>
                </div>
                <div className="p-3 rounded bg-white dark:bg-slate-800">
                  <p className="text-xs text-slate-400">Simulated Cost</p>
                  <p className="font-semibold text-emerald-600">{formatCurrency(simulationResult.simulated_cost)}</p>
                </div>
                <div className="p-3 rounded bg-white dark:bg-slate-800">
                  <p className="text-xs text-slate-400">Coverage Applied</p>
                  <p className="font-semibold">{simulationResult.coverage_percent}%</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scenarios Chart */}
      <Card data-testid="scenarios-chart">
        <CardHeader>
          <CardTitle className="text-lg">Historical Scenario Comparison</CardTitle>
          <CardDescription>Actual vs. simulated costs for each scenario</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
              <BarChart data={scenarioChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-20} textAnchor="end" height={80} tick={{fontSize: 11}} />
                <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="actual" fill="#E11D48" name="Actual Cost" />
                <Bar dataKey="simulated" fill="#059669" name="If Implemented" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Scenarios List */}
      <Card data-testid="scenarios-list">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-amber-500" />
            Saved Scenarios
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {scenarios.map((scenario, idx) => (
              <div key={idx} className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-amber-300 dark:hover:border-amber-700 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 dark:text-white">{scenario.name}</h4>
                    <p className="text-sm text-slate-400 mt-1">{scenario.description}</p>
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <span className="text-slate-400">Actual: <span className="font-medium text-rose-600">{formatCurrency(scenario.actual_cost)}</span></span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-400">Could be: <span className="font-medium text-emerald-600">{formatCurrency(scenario.simulated_cost)}</span></span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <Badge className="bg-emerald-100 text-emerald-700 text-lg px-3 py-1">
                      {formatCurrency(scenario.savings)} saved
                    </Badge>
                    <p className="text-sm text-slate-400 mt-1">{scenario.savings_percent}% reduction</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Insights */}
      <Card data-testid="insights">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            Key Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {insights.map((insight, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                <h4 className="font-semibold text-amber-900 dark:text-amber-100">{insight.title}</h4>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">{insight.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CostTimeMachine;
